import React, { useEffect, useState, useCallback, useRef } from 'react';
import { clinicAPI } from '../../services/api';
import { useNotify } from '../../context/NotifyContext';
import { LabDemoThemedTip } from '../Lab/LabDemographicsUi';
import Modal from '../Modal';

function triggerBlobDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function monthLabel(ym) {
  if (!/^\d{4}-\d{2}$/.test(String(ym || ''))) return 'All months';
  const [y, m] = String(ym).split('-').map(Number);
  const d = new Date(Date.UTC(y, m - 1, 1));
  return d.toLocaleDateString(undefined, { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

/** First and last calendar days of a YYYY-MM month (UTC), as YYYY-MM-DD for date inputs */
function monthBoundsUtc(ym) {
  if (!/^\d{4}-\d{2}$/.test(String(ym || ''))) return { from: '', to: '' };
  const [y, m] = String(ym).split('-').map(Number);
  const first = new Date(Date.UTC(y, m - 1, 1));
  const last = new Date(Date.UTC(y, m, 0));
  const iso = (d) => d.toISOString().slice(0, 10);
  return { from: iso(first), to: iso(last) };
}

/** Match lab demographics API page size (server max 500) */
const DEMO_PAGE_LIMIT = 500;

const emptyClinicDemoFilters = () => ({
  month: '',
  search: '',
  lab: '',
  day: '',
  dateFrom: '',
  dateTo: ''
});

const ClinicExtendedFeatures = ({ activeTab, labs = [] }) => {
  const { showError, showInfo } = useNotify();
  const [requests, setRequests] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [demoRows, setDemoRows] = useState([]);
  const [demoTotal, setDemoTotal] = useState(0);
  const [demoLoading, setDemoLoading] = useState(false);
  const [demoLoadingMore, setDemoLoadingMore] = useState(false);
  /** Calendar filters (month / single day / range) are omitted from the API until set — default is all linked rows */
  const [demoFilters, setDemoFilters] = useState(emptyClinicDemoFilters);
  const [demoSearchDebounced, setDemoSearchDebounced] = useState('');
  const [showDemoDateModal, setShowDemoDateModal] = useState(false);

  const nextDemoPageRef = useRef(2);
  const demoHasMoreRef = useRef(false);
  const demoLoadingMoreLockRef = useRef(false);
  const demoRowsLenRef = useRef(0);
  const demoTotalRef = useRef(0);
  const loadMoreSentinelRef = useRef(null);

  useEffect(() => {
    demoRowsLenRef.current = demoRows.length;
  }, [demoRows.length]);
  useEffect(() => {
    demoTotalRef.current = demoTotal;
  }, [demoTotal]);

  useEffect(() => {
    const id = window.setTimeout(() => setDemoSearchDebounced(demoFilters.search.trim()), 400);
    return () => window.clearTimeout(id);
  }, [demoFilters.search]);
  const [form, setForm] = useState({ labId: '', title: '', description: '', category: 'general', preferredAssigneeNote: '' });
  const [busy, setBusy] = useState(false);
  const [serviceSuccess, setServiceSuccess] = useState(null);

  const loadRequests = useCallback(async () => {
    try {
      const { data } = await clinicAPI.getServiceRequests();
      setRequests(data.requests || []);
    } catch (e) {
      console.error(e);
    }
  }, []);

  const loadAudit = useCallback(async () => {
    try {
      const { data } = await clinicAPI.getAuditLogs({ limit: 200 });
      setAuditLogs(data.logs || []);
    } catch (e) {
      console.error(e);
    }
  }, []);

  const buildDemoParams = useCallback(
    (page) => {
      const params = { limit: DEMO_PAGE_LIMIT, page };
      if (demoFilters.month) params.month = demoFilters.month;
      if (demoFilters.lab) params.labId = demoFilters.lab;
      if (demoFilters.day) params.day = demoFilters.day;
      if (demoFilters.dateFrom) params.dateFrom = demoFilters.dateFrom;
      if (demoFilters.dateTo) params.dateTo = demoFilters.dateTo;
      if (demoSearchDebounced) params.search = demoSearchDebounced;
      return params;
    },
    [
      demoFilters.month,
      demoFilters.day,
      demoFilters.dateFrom,
      demoFilters.dateTo,
      demoFilters.lab,
      demoSearchDebounced
    ]
  );

  const loadDemo = useCallback(async () => {
    setDemoLoading(true);
    demoLoadingMoreLockRef.current = false;
    try {
      const { data } = await clinicAPI.getDemographicsRows(buildDemoParams(1));
      const rows = data.rows || [];
      const total = data.total ?? rows.length;
      setDemoRows(rows);
      setDemoTotal(total);
      nextDemoPageRef.current = 2;
      demoHasMoreRef.current = rows.length < total;
    } catch (e) {
      console.error(e);
      const msg = e.response?.data?.message || e.message || 'Could not load demographics';
      showError(msg);
    } finally {
      setDemoLoading(false);
    }
  }, [buildDemoParams, showError]);

  const loadMoreDemo = useCallback(async () => {
    if (demoLoading || demoLoadingMoreLockRef.current) return;
    if (!demoHasMoreRef.current || demoTotalRef.current === 0) return;
    if (demoRowsLenRef.current >= demoTotalRef.current) return;
    demoLoadingMoreLockRef.current = true;
    setDemoLoadingMore(true);
    try {
      const page = nextDemoPageRef.current;
      const { data } = await clinicAPI.getDemographicsRows(buildDemoParams(page));
      const batch = data.rows || [];
      if (!batch.length) {
        demoHasMoreRef.current = false;
        return;
      }
      setDemoRows((prev) => {
        const next = [...prev, ...batch];
        demoHasMoreRef.current = next.length < demoTotalRef.current;
        return next;
      });
      nextDemoPageRef.current = page + 1;
    } catch (e) {
      console.error(e);
      const msg = e.response?.data?.message || e.message || 'Could not load more rows';
      showError(msg);
    } finally {
      setDemoLoadingMore(false);
      demoLoadingMoreLockRef.current = false;
    }
  }, [demoLoading, buildDemoParams, showError]);

  useEffect(() => {
    const el = loadMoreSentinelRef.current;
    if (!el || activeTab !== 'demographics') return undefined;
    const obs = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) loadMoreDemo();
      },
      { root: null, rootMargin: '280px', threshold: 0 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [activeTab, loadMoreDemo]);

  const clearDemoDateFilters = () => {
    setDemoFilters((d) => ({ ...d, day: '', dateFrom: '', dateTo: '' }));
  };

  const dateRowFiltersActive = !!(demoFilters.day || demoFilters.dateFrom || demoFilters.dateTo);

  useEffect(() => {
    if (activeTab === 'service-requests') loadRequests();
  }, [activeTab, loadRequests]);

  useEffect(() => {
    if (activeTab === 'audit-logs') loadAudit();
  }, [activeTab, loadAudit]);

  useEffect(() => {
    if (activeTab !== 'demographics') return;
    loadDemo();
  }, [activeTab, loadDemo]);

  const submitRequest = async (e) => {
    e.preventDefault();
    if (!form.labId || !form.title.trim()) {
      showInfo('Select a lab and enter a title.');
      return;
    }
    setBusy(true);
    try {
      await clinicAPI.createServiceRequest({
        labId: form.labId,
        title: form.title.trim(),
        description: form.description,
        submit: true,
        category: form.category || 'general',
        preferredAssigneeNote: form.preferredAssigneeNote || undefined
      });
      const labName = labs.find((l) => l._id === form.labId)?.labName || labs.find((l) => l._id === form.labId)?.name || 'Lab';
      setServiceSuccess({ title: form.title.trim(), labName });
      setForm({ labId: '', title: '', description: '', category: 'general', preferredAssigneeNote: '' });
      await loadRequests();
    } catch (err) {
      showError(err.response?.data?.message || 'Failed');
    } finally {
      setBusy(false);
    }
  };

  const downloadAtt = async (reqId, att) => {
    try {
      const res = await clinicAPI.downloadServiceAttachment(reqId, att._id);
      triggerBlobDownload(res.data, att.filename || 'attachment');
    } catch (e) {
      showError('Download failed');
    }
  };

  if (activeTab === 'service-requests') {
    return (
      <>
        <Modal isOpen={!!serviceSuccess} onClose={() => setServiceSuccess(null)} title="Request submitted">
          {serviceSuccess && (
            <div style={{ display: 'grid', gap: '12px' }}>
              <p style={{ margin: 0 }}>
                Your request <strong>{serviceSuccess.title}</strong> was sent to <strong>{serviceSuccess.labName}</strong>. You will see status updates here.
              </p>
              <button type="button" className="btn btn-primary" onClick={() => setServiceSuccess(null)}>OK</button>
            </div>
          )}
        </Modal>
        <div className="card" style={{ marginBottom: '24px' }}>
          <h2 className="card-title" style={{ marginBottom: '16px' }}>New request to lab</h2>
          <form onSubmit={submitRequest} style={{ display: 'grid', gap: '12px', maxWidth: '560px' }}>
            <select className="form-input" required value={form.labId} onChange={(e) => setForm((f) => ({ ...f, labId: e.target.value }))}>
              <option value="">Select lab</option>
              {labs.map((l) => (
                <option key={l._id} value={l._id}>{l.labName || l.name}</option>
              ))}
            </select>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Request type</label>
              <select className="form-input" value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}>
                <option value="general">General</option>
                <option value="phlebotomy">Phlebotomy / pickup</option>
                <option value="results">Results / reports</option>
                <option value="courier">Courier / logistics</option>
                <option value="supplies">Supplies</option>
                <option value="billing">Invoices & fees</option>
                <option value="other">Other</option>
              </select>
            </div>
            <input className="form-input" placeholder="Title (short summary)" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} required />
            <textarea className="form-textarea" placeholder="Details — include accession numbers, dates, or special instructions" rows={4} value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
            <input
              className="form-input"
              placeholder="Preferred phlebotomist or contact (optional note to lab)"
              value={form.preferredAssigneeNote}
              onChange={(e) => setForm((f) => ({ ...f, preferredAssigneeNote: e.target.value }))}
            />
            <button type="submit" className="btn btn-primary" disabled={busy}>Submit to lab</button>
          </form>

          <h3 style={{ marginTop: '32px', marginBottom: '12px' }}>Your requests ({requests.length})</h3>
          <div className="table-wrapper">
            <table className="table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Type</th>
                  <th>Lab</th>
                  <th>Assigned</th>
                  <th>Status</th>
                  <th>Attachments</th>
                </tr>
              </thead>
              <tbody>
                {requests.map((r) => (
                  <tr key={r._id}>
                    <td>
                      <div style={{ fontWeight: 500 }}>{r.title}</div>
                      {r.preferredAssigneeNote && (
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '4px' }}>Note: {r.preferredAssigneeNote}</div>
                      )}
                    </td>
                    <td><span className="badge badge-info">{r.category || 'general'}</span></td>
                    <td>{r.lab?.labName || r.lab?.name}</td>
                    <td>{r.assignedUser ? `${r.assignedUser.name}${r.assignedUser.role === 'phlebotomist' ? ' (phleb.)' : ''}` : '—'}</td>
                    <td><span className="badge badge-info">{r.status}</span></td>
                    <td>
                      {(r.attachments || []).map((a) => (
                        <button key={a._id} type="button" className="btn btn-outline" style={{ marginRight: '6px', fontSize: '0.75rem' }} onClick={() => downloadAtt(r._id, a)}>
                          {a.filename}
                        </button>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!requests.length && <p style={{ color: 'var(--text-secondary)' }}>No requests yet.</p>}
        </div>
      </>
    );
  }

  if (activeTab === 'demographics') {
    return (
      <div className="card clinic-demographics-card">
        <Modal isOpen={showDemoDateModal} onClose={() => setShowDemoDateModal(false)} title="Row dates">
          <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: '16px' }}>
            Narrow rows by specimen draw/receive day (same rules as your lab&apos;s demographics sheet). Does not change lab data.
          </p>
          <div style={{ display: 'grid', gap: '12px', maxWidth: '400px' }}>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label" htmlFor="clinic-demo-day">Single day</label>
              <input
                id="clinic-demo-day"
                type="date"
                className="form-input"
                value={demoFilters.day}
                onChange={(e) => {
                  const v = e.target.value;
                  setDemoFilters((d) => ({
                    ...d,
                    day: v,
                    ...(v ? { dateFrom: '', dateTo: '' } : {})
                  }));
                }}
              />
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label" htmlFor="clinic-demo-rfrom">From</label>
              <input
                id="clinic-demo-rfrom"
                type="date"
                className="form-input"
                value={demoFilters.dateFrom}
                onChange={(e) => {
                  const v = e.target.value;
                  setDemoFilters((d) => ({ ...d, dateFrom: v, ...(v ? { day: '' } : {}) }));
                }}
              />
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label" htmlFor="clinic-demo-rto">To</label>
              <input
                id="clinic-demo-rto"
                type="date"
                className="form-input"
                value={demoFilters.dateTo}
                min={demoFilters.dateFrom || undefined}
                onChange={(e) => {
                  const v = e.target.value;
                  setDemoFilters((d) => ({ ...d, dateTo: v, ...(v ? { day: '' } : {}) }));
                }}
              />
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              <button
                type="button"
                className="btn btn-outline"
                onClick={() => {
                  if (!demoFilters.month) {
                    showInfo('Choose a working month in filters first.');
                    return;
                  }
                  const { from, to } = monthBoundsUtc(demoFilters.month);
                  setDemoFilters((d) => ({ ...d, day: '', dateFrom: from, dateTo: to }));
                }}
              >
                Fill range from month
              </button>
              <button type="button" className="btn btn-outline" onClick={clearDemoDateFilters}>
                Clear dates
              </button>
              <button type="button" className="btn btn-primary" onClick={() => setShowDemoDateModal(false)}>
                Done
              </button>
            </div>
          </div>
        </Modal>

        <h2 className="card-title lab-demographics-sheet__title" style={{ marginBottom: '8px' }}>
          Demographics linked to your clinic
        </h2>
        <p className="lab-demographics-sheet__intro" style={{ marginBottom: '12px' }}>
          When your labs link rows to you (auto-match on client # / medical group name, or manual link), those rows appear here.
          All linked rows load automatically. Working month, row dates (single day or range), and lab/search narrow the list—calendar filters are sent only when you set them.
          Extra rows load as you scroll.
        </p>

        <div
          className="lab-demo-filters clinic-demo-filters-sticky"
          role="region"
          aria-label="Clinic demographics filters"
          style={{
            position: 'sticky',
            top: 0,
            zIndex: 6,
            marginBottom: '12px',
            paddingTop: '8px',
            paddingBottom: '10px',
            marginLeft: '-4px',
            marginRight: '-4px',
            paddingLeft: '4px',
            paddingRight: '4px',
            background: 'linear-gradient(to bottom, var(--bg-secondary) 88%, transparent)'
          }}
        >
          <div className="lab-demo-filters__block">
            <p className="lab-demo-filters__heading" style={{ marginBottom: '10px' }}>
              Filters
            </p>
            <div className="lab-demo-filters__grid lab-demo-filters__grid--primary">
              <div className="form-group lab-demo-filters__field" style={{ margin: 0 }}>
                <label className="form-label" htmlFor="clinic-demo-month">Working month</label>
                <input
                  id="clinic-demo-month"
                  type="month"
                  className="form-input"
                  value={demoFilters.month}
                  onChange={(e) => setDemoFilters((d) => ({ ...d, month: e.target.value }))}
                  title="Leave blank for all months; set to filter by calendar month"
                />
              </div>
              {labs.length > 1 && (
                <div className="form-group lab-demo-filters__field" style={{ margin: 0 }}>
                  <label className="form-label" htmlFor="clinic-demo-lab">Lab</label>
                  <select
                    id="clinic-demo-lab"
                    className="form-input"
                    value={demoFilters.lab}
                    onChange={(e) => setDemoFilters((d) => ({ ...d, lab: e.target.value }))}
                  >
                    <option value="">All linked labs</option>
                    {labs.map((l) => (
                      <option key={l._id} value={l._id}>{l.labName || l.name}</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="form-group lab-demo-filters__field lab-demo-filters__search" style={{ margin: 0 }}>
                <label className="form-label" htmlFor="clinic-demo-search">Search</label>
                <input
                  id="clinic-demo-search"
                  type="search"
                  className="form-input"
                  placeholder="Accession, patient, panel…"
                  value={demoFilters.search}
                  onChange={(e) => setDemoFilters((d) => ({ ...d, search: e.target.value }))}
                  autoComplete="off"
                />
              </div>
              <button
                type="button"
                className="btn btn-outline lab-demo-filters__clear"
                onClick={() => setShowDemoDateModal(true)}
              >
                Row dates{dateRowFiltersActive ? ' (active)' : ''}
              </button>
              <button
                type="button"
                className="btn btn-outline"
                onClick={() => setDemoFilters(emptyClinicDemoFilters())}
              >
                Clear filters
              </button>
            </div>
          </div>
        </div>

        <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '8px' }}>
          {demoLoading ? (
            <span>Loading…</span>
          ) : (
            <>
              <strong>{demoRows.length}</strong> / <strong>{demoTotal}</strong> rows loaded
              {' · '}
              up to {DEMO_PAGE_LIMIT} per request ·{' '}
              <strong>
                {demoFilters.month
                  ? monthLabel(demoFilters.month)
                  : dateRowFiltersActive
                    ? 'Row date filter'
                    : 'All months'}
              </strong>
              {dateRowFiltersActive && (
                <>
                  {' '}
                  · day/range ·{' '}
                  {demoFilters.day && <span>day {demoFilters.day}</span>}
                  {(demoFilters.dateFrom || demoFilters.dateTo) && (
                    <span>
                      {demoFilters.dateFrom || '…'} → {demoFilters.dateTo || '…'}
                    </span>
                  )}
                </>
              )}
              {demoFilters.lab ? ' · lab filter' : ''}
              {demoSearchDebounced ? ' · search active' : ''}
              {!demoFilters.month &&
                !dateRowFiltersActive &&
                !demoFilters.lab &&
                !demoSearchDebounced &&
                ' · no filters — all linked rows'}
              {demoRows.length < demoTotal && demoTotal > 0 && ' · scroll to load more'}
              {!demoLoading && demoTotal > 0 && demoRows.length >= demoTotal && ' · all rows loaded'}
            </>
          )}
        </p>
        <div className="table-wrapper lab-demo-table-scroll" style={{ overflowX: 'auto' }}>
          <table className="table">
            <thead>
              <tr>
                <th>Lab</th>
                <th>Accession</th>
                <th>Patient</th>
                <th>DOB</th>
                <th>Description</th>
                <th>Medical group</th>
                <th>Panel</th>
                <th>Result / Payment</th>
              </tr>
            </thead>
            <tbody>
              {demoRows.map((row) => (
                <tr key={row._id}>
                  <td style={{ fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
                    {row.lab?.labName || row.lab?.name || '—'}
                  </td>
                  <td style={{ fontFamily: 'monospace' }}>
                    <LabDemoThemedTip text={String(row.specimenId || '')}>{row.specimenId}</LabDemoThemedTip>
                  </td>
                  <td style={{ fontSize: '0.85rem' }}>
                    <LabDemoThemedTip
                      text={[row.patientFirst, row.patientLast].filter(Boolean).join(' ') || row.patientLine || '—'}
                    >
                      <span>{[row.patientFirst, row.patientLast].filter(Boolean).join(' ') || row.patientLine || '—'}</span>
                    </LabDemoThemedTip>
                  </td>
                  <td style={{ fontSize: '0.85rem' }}>
                    <LabDemoThemedTip text={String(row.dobRaw || '—')}>{row.dobRaw || '—'}</LabDemoThemedTip>
                  </td>
                  <td style={{ fontSize: '0.8rem', maxWidth: '240px' }}>
                    <LabDemoThemedTip text={String(row.exportDescription || '')}>{row.exportDescription}</LabDemoThemedTip>
                  </td>
                  <td>
                    <LabDemoThemedTip text={String(row.exportMedicalGroup || '')}>{row.exportMedicalGroup}</LabDemoThemedTip>
                  </td>
                  <td style={{ fontSize: '0.8rem' }}>
                    <LabDemoThemedTip text={String(row.exportPanel || '')}>{row.exportPanel}</LabDemoThemedTip>
                  </td>
                  <td style={{ fontSize: '0.8rem' }}>
                    <LabDemoThemedTip text={`${row.exportResultStatus || ''} / ${row.exportBillingStatus || ''}`}>
                      {row.exportResultStatus} / {row.exportBillingStatus}
                    </LabDemoThemedTip>
                  </td>
                </tr>
              ))}
              <tr ref={loadMoreSentinelRef} aria-hidden style={{ height: 1, border: 'none' }}>
                <td colSpan={8} style={{ padding: 0, border: 'none', height: 1 }} />
              </tr>
            </tbody>
          </table>
        </div>
        {demoLoadingMore && (
          <p style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', marginTop: '8px', marginBottom: 0 }}>
            Loading more rows…
          </p>
        )}
        {!demoLoading && !demoRows.length && (
          <p style={{ color: 'var(--text-secondary)' }}>
            No rows for this view. Try clearing month or row-date filters, or confirm your lab linked these records to your clinic account.
          </p>
        )}
      </div>
    );
  }

  if (activeTab === 'audit-logs') {
    return (
      <div className="card">
        <h2 className="card-title" style={{ marginBottom: '16px' }}>Audit log</h2>
        <div className="table-wrapper" style={{ overflowX: 'auto' }}>
          <table className="table">
            <thead>
              <tr>
                <th>Time</th>
                <th>Action</th>
                <th>Entity</th>
                <th>User</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {auditLogs.map((log) => (
                <tr key={log._id}>
                  <td style={{ whiteSpace: 'nowrap', fontSize: '0.85rem' }}>{new Date(log.timestamp).toLocaleString()}</td>
                  <td><span className="badge badge-info">{log.action}</span></td>
                  <td>{log.entity}</td>
                  <td>{log.userName}</td>
                  <td style={{ fontSize: '0.85rem' }}>{log.details}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!auditLogs.length && <p style={{ color: 'var(--text-secondary)' }}>No log entries yet.</p>}
      </div>
    );
  }

  return null;
};

export default ClinicExtendedFeatures;
